import type { AnySceneObjectT, SimpleStyleT } from "tweenly"
import { useEffect, useMemo, useState } from "react"
import { ObjectStyles, getObjectPositioning } from "../../helpers/object.helpers"
import { isAxisAligned, Point, toLocalDelta } from "../../helpers/orientedResize.helpers"
import { ancestorsTransform } from "../../helpers/selectionChrome.helpers"
import { selectUndoable } from "../../helpers/selector.helpers"
import { NOT_SNAPPED, SnappedAxes } from "../../helpers/snap.helpers"
import { createSnapSession, SnapSession } from "../../helpers/snapSession.helpers"
import { roundTo2 } from "../../helpers/transform.helpers"
import { selectHasMultiSelection } from "../../store/slices/activeObject.slice"
import { updateObjectsPositionAction } from "../../store/slices/objects.slice"
import { UpdateObjectsDifferentStyles } from "../../store/slices/objects.slice/object/objectTypes"
import useDynamicRefs from "../useDynamicRefs"
import { useObjectActions } from "../useObjectActions"
import { useAppDispatch, useAppSelector, useAppStore } from "../useRedux"
import type { HoldDragAxis, HoldDragDelta } from "./useHoldDrag"
import useWrapSelectedObject from "./useWrapSelectedObject"
import useWrapSelectedObjects, { ObjectStylesWithId } from "./useWrapSelectedObjects"

const DEFAULT_OBJECT_POSITION = { position: "absolute", top: 0, left: 0, width: 0, height: 0 }
export const useDraggable = (
    object: AnySceneObjectT | undefined,
    canvasScaleCoef: number,
    selectedObjects: AnySceneObjectT[] | undefined
) => {
    //general const
    const dispatch = useAppDispatch()
    const store = useAppStore()
    const [getRef] = useDynamicRefs<HTMLElement>()
    const [dragging, setDragging] = useState<boolean>(false)
    const [draggableAxis, setDraggableAxis] = useState<"x" | "y" | undefined>(undefined)
    const { updateObjectStyles } = useObjectActions()
    // primitives, not the whole `editor.value` object: this hook runs in every canvas
    // container, so subscribing to the object made all of them re-render whenever any
    // unrelated editor setting changed
    const canvasZoom = useAppSelector((state) => state.editor.value.settings.canvasZoom)
    const canvasSnapToGrid = useAppSelector(
        (state) => state.editor.value.settings.canvasSnapToGridOn
    )
    const canvasSnapGrid = canvasSnapToGrid ? 20 : 1

    //single object const — scoped to this object, so the ~200 non-selected containers that
    //also run this hook do not recompute (or re-render) on every selection change
    const { wrapperObjectsStyles } = useWrapSelectedObject(object?.id)

    //multiObjects const — the full multiselection geometry is only needed by the wrapper drag
    //itself, which is the single call site that passes `selectedObjects`
    const isMultiWrapperDrag = selectedObjects !== undefined
    const { wrapperObject } = useWrapSelectedObjects(isMultiWrapperDrag)
    const wrapperExist = useAppSelector(selectHasMultiSelection)

    const draggableScale = (canvasScaleCoef * canvasZoom) / 100

    /**
     * Snap the object position to the grid, unless the drag is shift-locked to the other axis
     * or the axis has already been snapped to another object.
     *
     * The locked coordinate has to be returned untouched — snapping a zero delta is NOT the
     * same thing. With a 20px grid `Math.round(37 / 20) * 20` moves an object that never
     * travelled along that axis to 40, so a shift-locked horizontal drag would silently
     * shift the object vertically as well.
     *
     * An object-snapped coordinate is kept as it is (bar the float noise of measuring the DOM):
     * rounding it onto the grid — or even to a whole pixel, when the snap lined up two centres
     * half a pixel apart — would land the object off the guide the user has just seen.
     *
     * @param lockedAxis the axis the gesture is restricted to, if any
     * @param axis the axis this call computes
     * @param snapped which axes the drag pulled onto another object
     */
    const getSnappedPosition = (
        lockedAxis: HoldDragAxis,
        axis: "x" | "y",
        current: number,
        delta: number,
        snapped: SnappedAxes
    ): number => {
        if (lockedAxis !== undefined && lockedAxis !== axis) return current
        if (snapped[axis]) return Math.round((current + delta) * 100) / 100
        return Math.round((current + delta) / canvasSnapGrid) * canvasSnapGrid
    }

    /**
     * Where a drag of `delta` (canvas axes) takes the object, in its own `left` / `top`.
     *
     * Those live in the parent's space: inside a turned (or skewed) parent they run along its
     * sides, so the delta is turned back into them first — otherwise the object lands off at
     * the parent's angle. Grid snapping and the shift-lock only make sense along the canvas
     * axes, so in a turned parent the position is just kept to two decimals. A parent that is
     * only scaled or flipped keeps the axes, and so the snapping too.
     */
    const getDraggedPosition = (
        object: AnySceneObjectT,
        delta: HoldDragDelta,
        axis: HoldDragAxis,
        snapped: SnappedAxes
    ): Point => {
        const currentPosition = getObjectPositioning(object)
        const currentLeft = Number(currentPosition.left)
        const currentTop = Number(currentPosition.top)
        const element = getRef(object.id)?.current
        const parentTransform = element
            ? ancestorsTransform(element, getRef("canvas")?.current)
            : undefined
        const local = parentTransform ? toLocalDelta(parentTransform, delta) : delta

        if (parentTransform && !isAxisAligned(parentTransform)) {
            return { x: roundTo2(currentLeft + local.x), y: roundTo2(currentTop + local.y) }
        }
        return {
            x: getSnappedPosition(axis, "x", currentLeft, local.x, snapped),
            y: getSnappedPosition(axis, "y", currentTop, local.y, snapped),
        }
    }

    /**
     * Snap to objects for the drag that is just beginning — the whole multiselection, or this
     * one object. The setting is read from the store here rather than subscribed to, so the
     * ~200 containers running this hook do not re-render when it is toggled.
     */
    const getSnapSession = (): SnapSession | null => {
        const state = store.getState()
        if (!state.editor.value.settings.canvasSnapToObjectsOn) return null
        const movingIds = selectedObjects
            ? selectedObjects.map((selected) => selected.id)
            : object
            ? [object.id]
            : []
        return createSnapSession(movingIds, selectUndoable(state).objects.value)
    }

    /**
     * recursive function for check parentIds up to rootId
     * @param object current object
     * @param selectedObjectIds read from the store when the drag ends, so this hook does not
     * have to subscribe to the selection in all ~200 containers that instantiate it
     * @param objectsById likewise read from the store on drag end — `useObject()` would have
     * subscribed every container to the objects map
     * @returns true if parentId is in selectedObjectIds, else false
     */
    const handleCheckParentIdInSelectedIds = (
        object: AnySceneObjectT,
        selectedObjectIds: string[],
        objectsById: Record<string, AnySceneObjectT>
    ): boolean => {
        if (object.parentId) {
            if (selectedObjectIds.find((id) => id === object.parentId)) {
                return true
            } else {
                const parentObject = objectsById[object.parentId]
                if (parentObject) {
                    return handleCheckParentIdInSelectedIds(
                        parentObject,
                        selectedObjectIds,
                        objectsById
                    )
                } else {
                    return false
                }
            }
        } else {
            return false
        }
    }

    /**
     * Commit a finished drag.
     *
     * @param delta travelled distance in canvas units, already axis-constrained by the gesture
     * @param axis the axis the gesture was shift-locked to, if any
     * @param snapped which axes the drag pulled onto another object
     */
    const handleDragEnd = (
        delta: HoldDragDelta,
        axis: HoldDragAxis,
        snapped: SnappedAxes = NOT_SNAPPED
    ) => {
        if (wrapperExist && selectedObjects) {
            const state = store.getState()
            const selectedObjectIds = state.activeObject.selected
            const objectsById = selectUndoable(state).objects.value
            let temporaryObjects: UpdateObjectsDifferentStyles[] = []
            selectedObjects
                .filter(
                    (object) =>
                        !handleCheckParentIdInSelectedIds(object, selectedObjectIds, objectsById)
                )
                .forEach((object) => {
                    const currentPosition = getObjectPositioning(object)
                    const { x: newLeft, y: newTop } = getDraggedPosition(
                        object,
                        delta,
                        axis,
                        snapped
                    )
                    const currentLeft = Number(currentPosition.left)
                    const currentTop = Number(currentPosition.top)

                    if (newLeft !== currentLeft || newTop !== currentTop) {
                        let newStyles: SimpleStyleT[] = [...object.styles]

                        newStyles = newStyles.map((style) => {
                            if (style.property === "left") {
                                return { ...style, value: newLeft }
                            }
                            if (style.property === "top") {
                                return { ...style, value: newTop }
                            }
                            return style
                        })
                        temporaryObjects.push({ object, property: "styles", value: newStyles })
                    }
                })

            if (temporaryObjects.length > 0) {
                dispatch(updateObjectsPositionAction(temporaryObjects))
            }
        } else if (!wrapperExist && object) {
            const currentPosition = getObjectPositioning(object)
            const { x: newLeft, y: newTop } = getDraggedPosition(object, delta, axis, snapped)

            const currentLeft = Number(currentPosition.left)
            const currentTop = Number(currentPosition.top)

            if (newLeft !== currentLeft || newTop !== currentTop) {
                updateObjectStyles(object, {
                    left: newLeft,
                    top: newTop,
                })
            }
        }
        setDragging(false)
        setDraggableAxis(undefined)
    }
    /**
     * Mirror the gesture's shift-lock into state, purely so the cursor can reflect it.
     *
     * `useHoldDrag` derives the axis itself (it needs it to constrain the preview transform)
     * and calls this only when the lock actually changes, never once per pointermove — an
     * unconditional setState here would re-render the container and its 8 resize handles on
     * every mouse move.
     */
    const handleDragAxisChange = (axis: HoldDragAxis) => {
        setDraggableAxis(axis)
    }

    const setCursorWhenDragging = useMemo(
        () =>
            draggableAxis
                ? draggableAxis === "x"
                    ? "ew-resize"
                    : draggableAxis === "y"
                    ? "ns-resize"
                    : "pointer"
                : dragging
                ? "move"
                : "pointer",
        [draggableAxis, dragging]
    )

    const handleOnStartDrag = () => {
        if (!dragging) setDragging(true)
    }

    //used for correct wrapping object with child when move and after moved (avoid of flickering)
    const [singleWrapBeforeUpdate, setSingleWrapBeforeUpdate] = useState<
        ObjectStylesWithId | undefined
    >(undefined)

    //used for correct wrapping objects with child when move and after moved (avoid of flickering)
    const [multiWrapBeforeUpdate, setMultiWrapBeforeUpdate] = useState<ObjectStyles | undefined>(
        undefined
    )
    useEffect(() => {
        if (wrapperExist) {
            if (dragging) {
                setMultiWrapBeforeUpdate(wrapperObject)
            } else {
                setMultiWrapBeforeUpdate(undefined)
            }
        } else {
            if (dragging) {
                setSingleWrapBeforeUpdate(wrapperObjectsStyles)
            } else {
                setSingleWrapBeforeUpdate(undefined)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dragging, wrapperExist])

    const defaultSingleWrapperState =
        !dragging &&
        wrapperObjectsStyles.top !== singleWrapBeforeUpdate?.top &&
        wrapperObjectsStyles.left !== singleWrapBeforeUpdate?.left

    const defaultMultiWrapperState =
        !dragging &&
        wrapperObject.top !== multiWrapBeforeUpdate?.top &&
        wrapperObject.left !== multiWrapBeforeUpdate?.left

    // stable reference while the object does not change, so the 8 memoized DraggableItem
    // resize handles can bail out of re-rendering
    const objectPositioning = useMemo(
        () => (object ? getObjectPositioning(object) : DEFAULT_OBJECT_POSITION),
        [object]
    )

    return {
        handleDragEnd,
        handleDragAxisChange,
        handleOnStartDrag,
        getSnapSession,
        setCursorWhenDragging,
        dragging,
        draggableAxis,
        draggableScale,
        objectPositioning,
        defaultSingleWrapperState,
        defaultMultiWrapperState,
    }
}
